import React, { useCallback, useMemo, memo } from 'react';
import { Table, Space, Card, Tooltip } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useHomepageSliders from '../useHomepageSliders';
import { HomepageSlidersFilters } from './HomepageSlidersFilters';
import { EmptyState } from './EmptyState';
import { Button } from 'components';

export const HomepageSlidersList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);

  const {
    sliders,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deleteSlider,
    shoppingCenters,
  } = useHomepageSliders();
  const handleDelete = useCallback(
    async (id) => {
      try {
        const success = await deleteSlider(id);
        if (success) {
          window.location.reload();
        }
      } catch (err) {
        console.error('Error deleting slider:', err);
      }
    },
    [deleteSlider],
  );

  const handleFiltersChange = useCallback(
    (newFilters) => {
      updateFilters(newFilters);
    },
    [updateFilters],
  );

  const columns = useMemo(
    () => [
      {
        title: <div style={{ textAlign: 'center' }}>{t('Titolo')}</div>,
        dataIndex: 'title',
        key: 'title',
        render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
        align: 'center',
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Descrizione')}</div>,
        dataIndex: 'description',
        key: 'description',
        ellipsis: true,
        align: 'center',
      },
      {
        title: (
          <div style={{ textAlign: 'center' }}>{t('Outlet Village')}</div>
        ),
        dataIndex: 'shopping_center',
        key: 'shopping_center',
        align: 'center',
        render: (shoppingCenter) => {
          if (shoppingCenter?.title) {
            return shoppingCenter.title;
          }
          return '-';
        },
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Immagine/Video')}</div>,
        dataIndex: 'file',
        key: 'file',
        align: 'center',
        render: (file) => {
          if (file?.url) {
            // Check if it's a video file
            const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.3gp', '.mpeg'];
            const isVideo = videoExtensions.some(ext => 
              file.file_name?.toLowerCase().includes(ext) || 
              file.url?.toLowerCase().includes(ext)
            );
            
            if (isVideo) {
              return (
                <div style={{ textAlign: 'center', position: 'relative' }}>
                  <video
                    src={file.url}
                    style={{
                      width: 48,
                      height: 48,
                      objectFit: 'cover',
                      borderRadius: 6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    }}
                    muted
                    preload='metadata'
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 20,
                      height: 20,
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <svg
                      width='12'
                      height='12'
                      viewBox='0 0 24 24'
                      fill='currentColor'
                    >
                      <path d='M8 5v14l11-7z'/>
                    </svg>
                  </div>
                </div>
              );
            } else {
              return (
                <img
                  src={file.url}
                  alt={file.file_name}
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: 'cover',
                    borderRadius: 6,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  }}
                />
              );
            }
          }
          return '-';
        },
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Posizione')}</div>,
        dataIndex: 'position',
        key: 'position',
        width: 100,
        align: 'center',
      },
      {
        title: <div style={{ textAlign: 'center' }}>{t('Azioni')}</div>,
        key: 'actions',
        width: 180,
        align: 'center',
        render: (_, record) => {
          if (!record || !record.id) {
            return <span>{t('Nessuna azione disponibile')}</span>;
          }
          return (
            <Space size='small'>
              {(pages?.ability?.can_read !== false ||
                record.can_read !== false) && (
                <Tooltip title={t('Visualizza')}>
                  <Button
                    type='secondary'
                    icon={<EyeOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/website/homepage_slides/${record.id}`);
                    }}
                  />
                </Tooltip>
              )}
              {record.can_update !== false && (
                <Tooltip title={t('Modifica')}>
                  <Button
                    type='secondary'
                    icon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/website/homepage_slides/edit/${record.id}`,
                      );
                    }}
                  />
                </Tooltip>
              )}
              {record.can_destroy !== false && (
                <Tooltip title={t('Elimina')}>
                  <Button
                    type='red'
                    icon={<DeleteIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(record.id);
                    }}
                  />
                </Tooltip>
              )}
            </Space>
          );
        },
      },
    ],
    [t, navigate, handleDelete, pages?.ability?.can_read],
  );

  if (!loading && sliders.length === 0) {
    return (
      <Card title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Homepage Sliders')}
        </span>
      }>
        <HomepageSlidersFilters
          onFiltersChange={handleFiltersChange}
          shoppingCenters={shoppingCenters}
          loadingCompanies={loadingCompanies}
          filters={filters}
        />
        <EmptyState />
      </Card>
    );
  }

  return (
        <Card
      title={
        <span style={{ fontSize: '24px', fontWeight: '600' }}>
          {t('Homepage Sliders')}
        </span>
      }
      extra={
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => navigate('/website/homepage_slides/create')}
          style={{ marginBottom: 12, width: '140px',height:48, backgroundColor: '#002637', borderColor: '#002637' }}
        >
          {t('Nuovo')}
        </Button>
      }
    >
      <HomepageSlidersFilters
        onFiltersChange={handleFiltersChange}
        shoppingCenters={shoppingCenters}
        loadingCompanies={loadingCompanies}
        filters={filters}
      />
        <Table
          columns={columns}
          dataSource={sliders}
          loading={loading}
          rowKey='id'
          onRow={(record) => ({
            onClick: () => {
              if (record?.id) {
                navigate(`/website/homepage_slides/${record.id}`);
              }
            },
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: false,
          }}
          onChange={(pagination) => {
            handlePageChange(pagination.current, pagination.pageSize);
          }}
          rowClassName={() => 'homepage-slider-row'}
          style={{ marginTop: 8 }}
          locale={{
            emptyText: t('Nessun dato trovato'),
          }}
        />
      <style>{`
        .homepage-slider-row:hover {
          background: #f5f7fa !important;
        }
      `}</style>
    </Card>
  );
});
